import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconButton } from '@fs/zion-ui'
import { ArrowCaret } from '@fs/zion-icon'
import { css } from '@emotion/core'
import { DmcResponsiveTable } from '../../common/DmcResponsiveTable'
import PersonChangeRow from './PersonChangeRow'
import { isNullOrUndefinedOrEmpty } from '../../../util/Utils'
import { getPersonChanges } from '../../../services/personService'
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll'
import SubLevelChanges from './SubLevelChanges'
import usePersonChangesTable from './usePersonChangesTable'

function renderRows(personChangesState, personChangesDispatch) {
  return personChangesState.changes.map((personChange) => {
    personChange.metadata.isTopGroupChange = null
    personChange.metadata.shouldHighlight = null
    personChange.metadata.noBottomDivider = null
    personChange.metadata.groupHidden = null
    return (
      <PersonChangeRow
        key={personChange.metadata.changeId}
        personChange={personChange}
        personChangesState={personChangesState}
        personChangesDispatch={personChangesDispatch}
      />
    )
  })
}

const getOpenGroups = (changeGroups) => {
  return Object.fromEntries(
    changeGroups.filter((group) => group.length > 1).map((group) => [group[group.length - 1].metadata.changeId, true])
  )
}

const handleToggleAll = (allOpen, changeGroups, setOpenGroups) => {
  const newState = !allOpen
  const newOpenGroups = {}
  changeGroups.forEach((group) => {
    if (group.length > 1) {
      const key = group[group.length - 1].metadata.changeId
      newOpenGroups[key] = newState
    }
  })
  setOpenGroups(newOpenGroups)
}

const caretCss = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export default function PersonChangesInfiniteScrolling({
  personId,
  personChangesState,
  personChangesDispatch,
  triggerRef,
  containerRef,
  rerenderInfiniteScroll,
  setRerenderInfiniteScroll,
  filtersState,
}) {
  const { t } = useTranslation()
  const infiniteScrollingCallbackRef = useRef()
  const { groupObjectsByChangeId } = usePersonChangesTable()

  infiniteScrollingCallbackRef.current = () => {
    if (isNullOrUndefinedOrEmpty(personChangesState.hasMoreChanges) || personChangesState.hasMoreChanges === false) {
      return
    }
    const lastChangeId = personChangesState.changes[personChangesState.changes.length - 1].metadata.changeId
    getPersonChanges(personId, lastChangeId).then((response) => {
      const action = {
        type: 'appendPersonChanges',
        state: response.data,
      }
      personChangesDispatch(action)
    })
  }

  useInfiniteScroll(
    containerRef,
    triggerRef,
    infiniteScrollingCallbackRef,
    IntersectionObserver,
    ResizeObserver,
    rerenderInfiniteScroll,
    setRerenderInfiniteScroll
  )

  const changeGroups = groupObjectsByChangeId(personChangesState)
  const [openGroups, setOpenGroups] = useState(() => getOpenGroups(changeGroups))

  useEffect(() => {
    if (changeGroups.filter((group) => group.length > 1).length > Object.keys(openGroups).length) {
      setOpenGroups((prev) => {
        const newEntries = getOpenGroups(changeGroups)
        return { ...newEntries, ...prev }
      })
    }
  }, [changeGroups, openGroups])

  const allOpen = Object.values(openGroups).every(Boolean)

  const caretHeader = (
    <span css={caretCss}>
      {t('new', 'New')}
      {changeGroups.filter((group) => group.length > 1).length > 0 && (
        <IconButton
          aria-label="toggle_All_Sub_Changes"
          data-testid="toggleAllSubChanges"
          Icon={ArrowCaret}
          iconDirection={allOpen ? 'up' : 'down'}
          onClick={() => handleToggleAll(allOpen, changeGroups, setOpenGroups)}
        />
      )}
    </span>
  )

  return (
    <DmcResponsiveTable
      id="personChangesTable"
      totalColumnCount={3}
      atSizeColumns={{
        default: {
          labels: [t('person.metadata', 'Metadata'), t('old', 'Old'), caretHeader],
        },
      }}
    >
      {personChangesState.changes &&
      filtersState?.currentFilters?.type === '' &&
      filtersState?.currentFilters?.subtypes.length === 0
        ? changeGroups.map((group) => {
            const [first, ...rest] = group.slice().reverse()
            first.metadata.noBottomDivider = rest.length > 0
            const result = [
              <PersonChangeRow
                key={first.metadata.changeId}
                personChange={first}
                personChangeGroup={group}
                personChangesState={personChangesState}
                personChangesDispatch={personChangesDispatch}
              />,
            ]
            if (rest.length > 0) {
              first.metadata.isTopGroupChange = true
              first.metadata.groupHidden = group.every((change) => change.metadata.hidden)
              result.push(
                <SubLevelChanges
                  openGroups={openGroups}
                  setOpenGroups={setOpenGroups}
                  topChange={first}
                  group={group}
                  changes={rest}
                  personChangesState={personChangesState}
                  personChangesDispatch={personChangesDispatch}
                />
              )
            }
            return result
          })
        : renderRows(personChangesState, personChangesDispatch)}
    </DmcResponsiveTable>
  )
}
